/**
 * Shared controls whose behaviour is specified, not just styled.
 *
 * `DueDateCell` is the one to read before changing: the design calls for a text
 * label plus a ✎ toggle, explicitly **not** a persistent inline date input, and
 * gives two reasons. One date picker per row puts fifty of them in the tab order;
 * and editing a due date is a distinct audited command, so a stray `change` event
 * on a bare input fires a real mutation nobody intended.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
  formatShortDate,
  formatMediumDate,
  dateOutOfWindow,
  dateWindow,
  isPhoneShaped,
  sanitiseDecimal,
  PHONE_MAX,
  PHONE_REFUSAL,
  type DateFieldPurpose,
} from "@truckbuddy/contract";
import { AlertTag } from "./primitives";
import {
  hasShortcutBlockingModifier,
  resolveDateShortcut,
  type DateFieldKind,
} from "../lib/date-shortcuts";

export function Seg<T extends string>({
  name,
  value,
  options,
  onChange,
  disabled = false,
  disabledReason,
}: {
  name: string;
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (value: T) => void;
  disabled?: boolean;
  /**
   * Why the control is off, said where a reader looks. A control that is greyed
   * out and silent is a defect; this puts the sentence in the tooltip and in the
   * accessible description, and only while `disabled` is true.
   */
  disabledReason?: string;
}) {
  const reasonId = useId();
  const reason = disabled ? disabledReason : undefined;
  return (
    <div
      className="seg"
      role="radiogroup"
      aria-label={name}
      aria-disabled={reason === undefined ? undefined : true}
      aria-describedby={reason === undefined ? undefined : reasonId}
      title={reason}
    >
      {reason === undefined ? null : (
        <span id={reasonId} className="sr-only">
          {reason}
        </span>
      )}
      {options.map((option) => (
        <label className="seg-opt" key={option.value}>
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            disabled={disabled}
            onChange={() => onChange(option.value)}
          />
          {option.label}
        </label>
      ))}
    </div>
  );
}

/**
 * A labelled form field.
 *
 * `children` may be a **render function** taking the generated id, which is how a
 * real `<label for>` association is possible: without it every control needs an
 * `aria-label` repeating the visible text, which screen readers then announce
 * twice. Pass a plain node when the control is not focusable and the association
 * does not apply.
 */
export function Field({
  label,
  children,
  hint,
  style,
}: {
  label: string;
  children: ReactNode | ((id: string) => ReactNode);
  hint?: string;
  style?: React.CSSProperties;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div className="field" style={style}>
      <label htmlFor={id}>{label}</label>
      {typeof children === "function" ? children(id) : children}
      {hint ? (
        <div className="text-muted" id={hintId} style={{ fontSize: "var(--tb-text-xs)", marginTop: 3 }}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}

export function CheckRow({
  checked,
  ariaLabel,
  onChange,
  children,
  disabled = false,
}: {
  checked: boolean;
  ariaLabel?: string;
  onChange?: (checked: boolean) => void;
  children: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label className="radio">
      <input
        type="checkbox"
        aria-label={ariaLabel}
        checked={checked}
        disabled={disabled || !onChange}
        onChange={(event) => onChange?.(event.currentTarget.checked)}
      />
      <span className="dot" />
      {children}
    </label>
  );
}

/**
 * The keydown handler that gives a date input Brett's **T = tomorrow, N = today**.
 *
 * Exported as a handler rather than baked only into `DateField` because two call
 * sites are not plain controlled inputs: `DueDateCell` below is uncontrolled and
 * writes through a ref, and the two `datetime-local` dialogs keep their own time.
 * A second copy of the key matching is what would let one field disagree with
 * another about what T means.
 *
 * `apply` receives the resolved value. It is only called for T and N — every
 * other key returns without touching the event, which is what keeps ordinary
 * typing, Tab, Enter and the native picker (Space / Alt+Down / F4) working.
 *
 * `preventDefault` is deliberate even though a letter typed into a `type="date"`
 * field does nothing in any browser: it stops the keystroke reaching a parent
 * that might act on it, and it makes the interception explicit rather than
 * relying on the input's own indifference to letters.
 */
export function dateShortcutKeyDown(
  kind: DateFieldKind,
  apply: (value: string) => void,
): (event: React.KeyboardEvent<HTMLInputElement>) => void {
  return (event) => {
    if (hasShortcutBlockingModifier(event)) return;
    const next = resolveDateShortcut(event.key, kind, event.currentTarget.value, new Date());
    if (next === null) return;
    event.preventDefault();
    apply(next);
  };
}

/**
 * The class list the Clear control carries, exported so a test can assert it
 * lands inside the touch floor rather than a comment merely claiming it does.
 *
 * `responsive.css` ends with a `@media (pointer: coarse)` block, and BOTH of its
 * declarations are needed here. `.btn` is floored at `min-height: 44px
 * !important`; `.btn-icon` is given `min-width: 44px`. `industry.css` makes
 * `.btn-icon` a fixed `36px` square, so the height rule alone would leave a
 * 36x44 lozenge on a finger and the width rule alone a 44x36 one — `min-height`
 * and `min-width` each clamp the fixed value regardless of importance.
 *
 * The consequence for anyone editing this button: it carries NO inline sizing,
 * and must not. `.btn-icon`'s `min-width` is the one of the two declarations
 * that is not `!important`, so an inline `minWidth` — even a larger one — would
 * beat it and quietly take the control back off the 44px floor.
 */
export const CLEAR_BUTTON_CLASS = "btn btn-ghost btn-icon";

/**
 * Every date field in the product, so the shortcut cannot be missed on one of
 * them.
 *
 * That is the whole reason this exists as a component rather than as a rule to
 * remember: T and N are Brett's habit, and a product where nine fields honour
 * them and the tenth does not is more confusing than one where none do. Nine
 * `type="date"` inputs and three `datetime-local` ones render through here —
 * re-counted 15 September 2026 across the twelve `<DateField` call sites, and
 * still nine and three.
 *
 * ── Clear, because iOS Safari has none (TRBUD-260) ───────────────────
 *
 * Desktop Chrome and Firefox both draw a clear affordance inside
 * `<input type="date">`. **iOS Safari draws none**, so on an iPad a date the
 * picker has committed once can be changed but never removed — which Eden hit on
 * a field labelled "Due date (optional)" whose submit sends `null` for an empty
 * value. Empty is a state the user is entitled to reach, so the control is here
 * rather than at the one call site that reported it.
 *
 * It clears by calling `onChange("")` — exactly what an emptied input emits — so
 * no caller learns a new shape, and every `useState("")` behind these twelve
 * fields is already written for it. Focus returns to the input afterwards: the
 * button unmounts the instant the value goes empty, and without this the focused
 * element would be destroyed and focus would fall to `<body>`.
 *
 * ── Two rules about WHEN it appears, and why ─────────────────────────
 *
 * It appears only when the field HAS a value, and never when the field is
 * `required`, `aria-required`, `disabled` or `readOnly`. `required` and
 * `aria-required` both count because a dialog that validates itself — which is
 * all of them here — should be able to say "mandatory" without also taking on
 * native form validation and the `:invalid` styling that rides with the real
 * attribute. No call site sets either today, which is why a mandatory-in-spirit
 * field like "Work date" still offers Clear: it is not declared, and inferring
 * it from a dialog's own refusal message is not something this component can do.
 *
 * `disabled` is the one that already matters: `CustomFieldControl` disables a
 * RETIRED custom field, whose value "is kept and cannot be changed", and a Clear
 * button there would be a way to change it.
 *
 * ── The wrapper is UNCONDITIONAL, and that is load-bearing ───────────
 *
 * The `<span>` is always rendered even though the button is not. Were it to
 * appear only alongside the button, the input would change position in the React
 * tree the moment a date was picked — React would unmount the bare input and
 * mount a fresh one inside the new wrapper, destroying the focused element
 * mid-interaction. On iOS that closes the picker, which is the very failure this
 * control exists to relieve.
 *
 * ── What it deliberately does NOT do ─────────────────────────────────
 *
 * It does not clamp the resolved date to the field's own `min` / `max`. Several
 * of these fields cap themselves at today — "Work date" on the two cost dialogs,
 * "Down since" on the request dialog — so T there produces a value the field
 * rejects. That is correct: the shortcut is a typing accelerator and behaves
 * exactly as if the user had typed the date, and both of those dialogs already
 * answer a future date with a written error naming the limit. Clamping would
 * silently hand back today, making T indistinguishable from N and hiding the
 * refusal the user needs to see.
 *
 * Extra input attributes pass straight through, which is what lets a caller keep
 * its `max`, its `id` from `Field`'s render-prop form, its `aria-label` or its
 * `aria-invalid` without this component having to know about any of them. A
 * caller's own `onKeyDown` still runs — the shortcut is composed in front of it,
 * not in place of it.
 */
export function DateField({
  value,
  onChange,
  kind = "date",
  className = "input",
  onKeyDown,
  clearLabel,
  purpose,
  ...rest
}: {
  value: string;
  onChange: (value: string) => void;
  /** `datetime-local` moves only the date half and keeps the time. */
  kind?: DateFieldKind;
  /**
   * What this date is FOR, which is what gives it a window — TRBUD-267.
   *
   * `scheduled` is a date being planned toward and has only the typo floor;
   * `recorded` is a date something already happened on and cannot be in the
   * future. Omitting it leaves the field unbounded, which is what every call
   * site did before this existed.
   *
   * ── Why this does not refuse the keystroke ───────────────────────────
   *
   * An out-of-window value is marked invalid and explained beside the field; it
   * is NOT swallowed. Typing a year into a date input passes through states like
   * `0002-09-15` on the way to `2026-09-15`, so a control that refused the
   * moment the value left the window would make the field impossible to type
   * into — the fix would read as the worse bug.
   *
   * The actual refusal lives on the submit path, where the dialog already
   * answers a bad date with a sentence. Both ends call `dateOutOfWindow`, so the
   * inline warning and the refusal cannot disagree about where the limit is.
   */
  purpose?: DateFieldPurpose;
  /**
   * What the Clear control calls this field, when the input has no `aria-label`
   * of its own to borrow.
   *
   * Six of the twelve call sites take their visible label from `Field`'s
   * `<label for>` instead of an `aria-label`, and this component cannot read
   * that label at render time. They get the generic "Clear date", which is a
   * real name and enough on a screen with one date field. Pass this where two
   * of them are on the page at once — `InvoiceEntryForm` is the only such place
   * today — so the two buttons are told apart by name and not only by position.
   */
  clearLabel?: string;
} & Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "type"
>) {
  const shortcut = dateShortcutKeyDown(kind, onChange);
  const inputRef = useRef<HTMLInputElement>(null);
  const ariaRequired = rest["aria-required"];
  const required =
    rest.required === true || ariaRequired === true || ariaRequired === "true";
  const clearable =
    value !== "" && !required && rest.disabled !== true && rest.readOnly !== true;

  /*
   * The window, and the caller's own bounds on top of it.
   *
   * A caller's explicit `min` / `max` WINS. Several fields are tighter than their
   * purpose — "Work date" caps at the latest fleet day the ledger will take, which
   * is stricter than "not in the future" — and the purpose must not loosen them.
   *
   * `datetime-local` needs a time on its bounds or the browser ignores them
   * outright, so the floor takes the start of its day and the ceiling the end of
   * its. Anything else would refuse every time on the ceiling day itself.
   */
  const window = purpose ? dateWindow(purpose, new Date()) : null;
  const suffixMin = kind === "datetime-local" ? "T00:00" : "";
  const suffixMax = kind === "datetime-local" ? "T23:59" : "";
  const min = rest.min ?? (window ? `${window.min}${suffixMin}` : undefined);
  const max =
    rest.max ?? (window?.max != null ? `${window.max}${suffixMax}` : undefined);

  // The date half only: a `datetime-local` carries a time the window says nothing
  // about, and comparing the whole string against a date-only bound would refuse
  // every value with a clock on it.
  const datePart = value.slice(0, 10);
  const problem =
    purpose && datePart !== "" ? dateOutOfWindow(datePart, purpose, new Date()) : null;
  const problemId = `${rest.id ?? "date"}-window`;

  return (
    /*
     * The message is a SIBLING of `.date-field`, never a new wrapper around it.
     *
     * `.date-field` is `display: flex`, so a message inside it would sit on the
     * same row as the input. But the reason it goes outside rather than the
     * wrapper being changed is the one the Clear control already records: moving
     * the input in the React tree unmounts it and destroys the focused element
     * mid-interaction. A sibling appearing AFTER a stable first child leaves the
     * input exactly where it was.
     */
    <>
    <span className="date-field">
      <input
        {...rest}
        ref={inputRef}
        type={kind}
        className={className}
        min={min}
        max={max}
        aria-invalid={problem !== null ? "true" : rest["aria-invalid"]}
        aria-describedby={
          problem !== null
            ? [rest["aria-describedby"], problemId].filter(Boolean).join(" ")
            : rest["aria-describedby"]
        }
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
        onKeyDown={(event) => {
          shortcut(event);
          onKeyDown?.(event);
        }}
      />
      {clearable ? (
        <button
          type="button"
          className={CLEAR_BUTTON_CLASS}
          // The glyph is decorative; `aria-label` is the name, the same way
          // `DueDateCell`'s ✎ is labelled "Edit due date" below.
          aria-label={`Clear ${clearLabel ?? rest["aria-label"] ?? "date"}`}
          onClick={() => {
            onChange("");
            // The button is about to unmount — see the note above.
            inputRef.current?.focus();
          }}
        >
          ×
        </button>
      ) : null}
    </span>
    {problem !== null ? (
      <div className="field-problem" id={problemId} role="alert">
        {problem}
      </div>
    ) : null}
    </>
  );
}

/**
 * "due 8/12 ✎" — and only becoming an input once the user asks.
 *
 * `commitOnChange` fires on the input's `change`, which is what the design
 * specifies, but only while the editor is deliberately open. Closing on blur means
 * an abandoned edit does not sit in the tab order.
 *
 * ── T and N here set the value WITHOUT saving ────────────────────────
 *
 * The field is uncontrolled — `defaultValue` plus a commit on `change` — so the
 * shortcut writes through the ref, which is what makes the resolved date visible
 * before anything is written. Eden's spec asks for exactly that ("the resolved
 * date is visible before save"), and a programmatic value assignment fires no
 * `change` event, so the commit path is untouched by it.
 *
 * That leaves the user needing a way to accept what they can see, so **Enter
 * commits**. Without it T would set a date the cell then threw away on blur —
 * `change` never fires for a value the user did not type — and the shortcut would
 * appear to do nothing on the one screen Eden named. Escape still abandons.
 */
export function DueDateCell({
  dueDate,
  overdueDays,
  canEdit,
  onCommit,
  medium = false,
}: {
  dueDate: string | null;
  overdueDays: number | null;
  canEdit: boolean;
  onCommit: (dueDate: string | null) => void;
  medium?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dateShortcut = dateShortcutKeyDown("date", (next) => {
    if (inputRef.current) inputRef.current.value = next;
  });
  /** What the editor opened on, so a blur can tell "left alone" from "cleared". */
  const openedWith = dueDate ?? "";

  /**
   * A due date set from the queue is a STORED value, so it takes the same window
   * as the one in the dialogs — TRBUD-267.
   *
   * The cell is the width of a date and has nowhere to put a sentence, so a
   * refused value keeps the editor open, marks it invalid and carries the reason
   * on `title` rather than committing. Everything else in this file reports a bad
   * date beside the field; here that is not available, and committing a date the
   * user cannot see refused would be the worse of the two.
   */
  function commit(raw: string): void {
    const next = raw || null;
    const problem = next === null ? null : dateOutOfWindow(next, "scheduled", new Date());
    if (problem !== null) {
      setRefused(problem);
      return;
    }
    setRefused(null);
    setEditing(false);
    onCommit(next);
  }

  /**
   * Leaving the editor: save what is there if it changed, otherwise just close.
   *
   * A refused value is abandoned rather than saved -- the same outcome the old
   * blur had -- because committing a date the user has been told is out of
   * range would be worse than dropping it. Recomputed here rather than read
   * from `refused`, so a blur that lands before the state update settles still
   * judges the value actually in the field.
   */
  function leave(raw: string): void {
    setRefused(null);
    if (raw === openedWith || (raw !== "" && dateOutOfWindow(raw, "scheduled", new Date()) !== null)) {
      setEditing(false);
      return;
    }
    commit(raw);
  }

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="date"
        className="input"
        style={{ minHeight: 30, padding: "2px 8px", fontSize: "var(--tb-text-sm)", width: 150 }}
        defaultValue={dueDate ?? ""}
        aria-label="Due date"
        min={dateWindow("scheduled", new Date()).min}
        aria-invalid={refused !== null ? "true" : undefined}
        title={refused ?? undefined}
        onClick={(event) => event.stopPropagation()}
        /*
         * VALIDATE on change, never SAVE on change -- 18 September 2026.
         *
         * A browser date input is three segments, and it fires `change` every
         * time one of them becomes valid: typing a year fires on 0002, 0020,
         * 0202 and 2026, and stepping a segment with the arrow keys fires per
         * step. This handler used to commit on the first value inside the
         * window and close the editor with it, so editing an existing date
         * saved on the FIRST keystroke, and a year typed digit by digit saved
         * whichever in-window year the caret passed through -- which is how a
         * due date landed a year early and the OVERDUE badge appeared on
         * staging while the person was still typing. Now the value is checked
         * as it changes (so an out-of-window date is marked while it is still
         * on screen) and saved only on Enter or on leaving the field.
         */
        onChange={(event) => {
          const raw = event.currentTarget.value;
          setRefused(raw === "" ? null : dateOutOfWindow(raw, "scheduled", new Date()));
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.stopPropagation();
            setRefused(null);
            setEditing(false);
            return;
          }
          if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            commit(event.currentTarget.value);
            return;
          }
          // T and N write straight to the DOM node: the input is uncontrolled, so
          // there is no state to set, and an assignment fires no `change` — which
          // is what keeps the resolved date visible without saving it.
          dateShortcut(event);
        }}
        // Blur is now the ordinary way to save: pick a day from the picker,
        // click away, and it is written. An unchanged or refused value closes
        // the editor and writes nothing, as before.
        onBlur={(event) => leave(event.currentTarget.value)}
      />
    );
  }

  const label = dueDate
    ? medium
      ? formatMediumDate(dueDate)
      : `due ${formatShortDate(dueDate)}`
    : "no due date";

  return (
    <span
      style={{ fontSize: "var(--tb-text-sm)", display: "inline-flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}
    >
      {label}
      {canEdit ? (
        <button
          type="button"
          className="btn btn-ghost"
          aria-label="Edit due date"
          style={{ fontSize: "var(--tb-text-xs)", padding: "0 4px", minHeight: 32 }}
          onClick={(event) => {
            event.stopPropagation();
            setEditing(true);
          }}
        >
          ✎
        </button>
      ) : null}
      {overdueDays !== null ? (
        <AlertTag kind="overdue">
          OVERDUE · {overdueDays}D
        </AlertTag>
      ) : null}
    </span>
  );
}

/** A debounced search box, so typing does not fire a query per keystroke. */
export function SearchInput({
  value,
  onChange,
  placeholder,
  width = 220,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  width?: number;
}) {
  const [local, setLocal] = useState(value);

  useEffect(() => setLocal(value), [value]);
  useEffect(() => {
    if (local === value) return;
    const timer = setTimeout(() => onChange(local), 250);
    return () => clearTimeout(timer);
  }, [local, value, onChange]);

  return (
    <input
      className="input"
      style={{ width, minHeight: 32, fontSize: "var(--tb-text-sm)" }}
      placeholder={placeholder}
      aria-label={placeholder}
      value={local}
      onChange={(event) => setLocal(event.currentTarget.value)}
    />
  );
}

/**
 * Longest option label rendered before it is clipped.
 *
 * Several of these selects are fed from **free-text database columns** —
 * `asset.asset_type`, part stocking locations, vendor capabilities — so no length
 * is guaranteed by anything. `asset_type` currently holds two rows containing an
 * entire scraped forklift spec sheet, roughly 500 characters, and because a
 * `<select>` sized `width: auto` resolves to its widest option that one value
 * rendered a 2855px control inside a 951px viewport and put the whole page into
 * horizontal scroll.
 *
 * The CSS `max-width` on `.filter-row .input` is the layout guard; this is the
 * legibility one. A clipped label with the full value on `title` beats a 500-
 * character dropdown entry that cannot be read either way.
 */
const MAX_OPTION_LABEL = 60;

function clipLabel(label: string): string {
  const flat = label.replace(/\s+/g, " ").trim();
  return flat.length > MAX_OPTION_LABEL ? `${flat.slice(0, MAX_OPTION_LABEL - 1)}…` : flat;
}

export function Select<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  width,
}: {
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (value: T) => void;
  ariaLabel: string;
  width?: number | string;
}) {
  return (
    <select
      className="input"
      style={{ width: width ?? "auto", minHeight: 32, fontSize: "var(--tb-text-sm)" }}
      aria-label={ariaLabel}
      value={value}
      onChange={(event) => onChange(event.currentTarget.value as T)}
    >
      {options.map((option) => (
        // `value` is never clipped — it is the filter the server receives, and a
        // truncated one would match nothing. Only the label shortens.
        <option key={option.value} value={option.value} title={option.label}>
          {clipLabel(option.label)}
        </option>
      ))}
    </select>
  );
}

/* ────────────────────────────────────────────────────────────────────────
 * The three poka-yoke controls — TRBUD-268, 270, 271
 *
 * All three follow `DateField`'s precedent: one component every call site
 * renders through, so the rule cannot be honoured by nine fields and missed by
 * the tenth. Each is deliberately thin — the RULE lives in
 * `packages/contract/src/field-shapes.ts`, shared with the API, and these
 * supply only the behaviour a browser can add on top of it.
 * ──────────────────────────────────────────────────────────────────────── */

/** When a length-bounded field starts telling the user how much room is left. */
const COUNTER_SHOWS_WITHIN = 40;

/**
 * A free-text field that cannot take more than its column will hold.
 *
 * `maxLength` is REQUIRED, and that is the whole point of the component: an
 * unbounded text field is now a type error rather than an oversight. Before this,
 * 94 of the 117 inputs in `apps/web` had no bound at all, so a user found out
 * they had written too much only when the server said so.
 *
 * ── Take the bound from the contract, not from taste ─────────────────
 *
 * Callers pass `TEXT_MAX.name`, `TEXT_MAX.prose` and friends, which are the same
 * numbers `requests.ts` declares. Nothing here is a new policy — the server has
 * enforced these since those schemas were written. What is new is that the input
 * knows, so the limit is visible while typing instead of after Save. A field with
 * a bespoke bound (a 17-character VIN, a 3-letter currency code) passes the
 * number directly; that is why this takes a number rather than only a `TextKind`.
 *
 * ── The counter appears late, on purpose ─────────────────────────────
 *
 * A permanent "0 / 1000" on every notes box is noise that teaches people to stop
 * reading the area where real errors appear. It shows only within
 * `COUNTER_SHOWS_WITHIN` characters of the limit, which is the point at which it
 * becomes information.
 */
export function TextField({
  value,
  onChange,
  maxLength,
  multiline = false,
  className = "input",
  rows = 3,
  ...rest
}: {
  value: string;
  onChange: (value: string) => void;
  /** Required. `TEXT_MAX.<kind>` for the ordinary cases. */
  maxLength: number;
  /** Renders a `<textarea>`. Prose fields are the ones that need the room. */
  multiline?: boolean;
  className?: string;
  rows?: number;
} & Omit<
  React.InputHTMLAttributes<HTMLInputElement> & React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  "value" | "onChange" | "maxLength" | "className" | "rows"
>) {
  const remaining = maxLength - value.length;
  const showCounter = remaining <= COUNTER_SHOWS_WITHIN;
  const counterId = `${rest.id ?? "text"}-remaining`;

  const shared = {
    ...rest,
    className,
    maxLength,
    value,
    "aria-describedby": showCounter
      ? [rest["aria-describedby"], counterId].filter(Boolean).join(" ")
      : rest["aria-describedby"],
  };

  return (
    <>
      {multiline ? (
        <textarea
          {...(shared as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
          rows={rows}
          onChange={(event) => onChange(event.currentTarget.value)}
        />
      ) : (
        <input
          {...(shared as React.InputHTMLAttributes<HTMLInputElement>)}
          onChange={(event) => onChange(event.currentTarget.value)}
        />
      )}
      {showCounter ? (
        <div
          className="field-counter"
          id={counterId}
          /*
           * `polite`, not `assertive`, and not `role="alert"`: the limit is
           * information while there is still room, and only becomes a problem at
           * zero. An assertive live region would interrupt a screen-reader user
           * on every keystroke for the last forty characters.
           */
          aria-live="polite"
        >
          {remaining === 0
            ? `That is the longest this field can be (${maxLength}).`
            : `${remaining} character${remaining === 1 ? "" : "s"} left.`}
        </div>
      ) : null}
    </>
  );
}

/**
 * A number field that letters cannot get into — TRBUD-268.
 *
 * ── Why the filter runs on the VALUE, not on the keystroke ───────────
 *
 * The obvious implementation refuses the keypress. It is wrong in four ways that
 * all have the same shape: text reaches an input by routes that are not
 * keystrokes. Paste, IME composition (every non-Latin script), autofill,
 * dictation and a screen reader's own text entry all deliver a whole value with
 * no `keypress` to cancel. Blocking keys therefore blocks the letter AND the
 * assistive technology, while filtering the resulting value blocks only the
 * letter. `sanitiseDecimal` is a pure function of the value for exactly this
 * reason, and is tested against a pasted string.
 *
 * ── Why not `type="number"` ──────────────────────────────────────────
 *
 * Two existing decisions in this repo, both preserved. A number input reports an
 * EMPTY STRING for text the browser considers invalid, which makes a typo
 * indistinguishable from a cleared field and silently drops the value
 * (`CustomFieldControl`); and its spinner invites a scroll wheel to change a
 * figure by one without anyone noticing (`NewAssetDialog`). `inputMode` still
 * gives a touch device the numeric keyboard, which is all it was ever doing.
 *
 * ── `scale` is not a style choice ────────────────────────────────────
 *
 * It is the column's own precision, and `money.ts` documents the three that
 * exist: amounts 2, rates and unit costs 4, quantities 3. Passing the wrong one
 * truncates silently — a rate entered to four places would be cut to cents — so
 * call sites take it from the same constants the parsers use.
 */
export function NumericField({
  value,
  onChange,
  scale,
  allowNegative = false,
  replaceOnType = false,
  className = "input",
  ...rest
}: {
  value: string;
  onChange: (value: string) => void;
  /** Decimal places this kind of value carries. `AMOUNT_SCALE` and friends. */
  scale: number;
  /** Credits and reversals are first-class here; most fields still take none. */
  allowNegative?: boolean;
  /**
   * The value is a default nobody typed: the first edit replaces it rather than
   * adding to it, so typing 5 into a defaulted 1 gives 5, not 15 or 51. Shown
   * muted until then (`data-default` in industry.css).
   */
  replaceOnType?: boolean;
  className?: string;
} & Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "type" | "className"
>) {
  return (
    <input
      {...rest}
      className={className}
      data-default={replaceOnType ? "true" : undefined}
      // Still a hint, still useful: this is what puts the numeric keypad under a
      // mechanic's thumb. It is simply no longer the only thing standing between
      // a cost column and the word "abc".
      inputMode={scale > 0 ? "decimal" : "numeric"}
      value={value}
      onChange={(event) => {
        const input = event.currentTarget;
        const raw = replaceOnType ? typedOverDefault(input.value, value, input.selectionStart) : input.value;
        onChange(sanitiseDecimal(raw, scale, { allowNegative }));
      }}
    />
  );
}

/**
 * What was typed into a field holding a default, without the default.
 *
 * Read off the VALUE and the caret rather than a keystroke, for the reason
 * `NumericField` gives: paste and IME arrive as a whole value with no key to
 * look at. Whatever was inserted sits just before the caret, so the caret says
 * where it is whichever side of the default somebody typed on. A deletion
 * clears the default outright. A value that is not the default with something
 * inserted into it (a selection typed over, autofill, a whole value set at
 * once) is already the replacement, and is taken as it stands.
 */
export function typedOverDefault(raw: string, previous: string, caret: number | null): string {
  const inserted = raw.length - previous.length;
  if (inserted < 0) return "";
  if (inserted === 0 || caret === null) return raw;
  const start = caret - inserted;
  if (start < 0 || raw.slice(0, start) + raw.slice(caret) !== previous) return raw;
  return raw.slice(start, caret);
}

/**
 * Why this phone value would be refused, or `null` when it would not.
 *
 * Exported so a dialog's submit guard and the field itself cannot disagree. An
 * empty value returns `null` — no phone on record is a legitimate state, and the
 * one the majority of vendors are in.
 */
export function phoneShapeProblem(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  return isPhoneShaped(trimmed) ? null : PHONE_REFUSAL;
}

/**
 * A phone number, refused outright when it is not one — TRBUD-271.
 *
 * The predicate is `isPhoneShaped` from the contract, which is the same one the
 * API refuses with and the same one the vendor address import classifies Matt's
 * note cells with. Three readers, one rule.
 *
 * ── It warns while typing and refuses on submit ──────────────────────
 *
 * The message appears as soon as the value stops looking like a phone number, but
 * the keystroke is not swallowed — a half-typed number is not a phone number yet,
 * and a control that erased everything up to the tenth digit would be unusable.
 * `phoneShapeProblem` is exported so the dialog's submit guard asks the same
 * question, which is what makes the refusal real rather than advisory.
 *
 * ── Empty is a state, not a failure ──────────────────────────────────
 *
 * Most vendors have no phone on record — on staging that is the majority — and
 * "no phone" must stay reachable. Empty never warns.
 */
export function PhoneField({
  value,
  onChange,
  className = "input",
  ...rest
}: {
  value: string;
  onChange: (value: string) => void;
  className?: string;
} & Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "type" | "className" | "maxLength"
>) {
  const problem = phoneShapeProblem(value);
  const problemId = `${rest.id ?? "phone"}-problem`;

  return (
    <>
      <input
        {...rest}
        className={className}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        maxLength={PHONE_MAX}
        aria-invalid={problem !== null ? "true" : undefined}
        aria-describedby={
          problem !== null
            ? [rest["aria-describedby"], problemId].filter(Boolean).join(" ")
            : rest["aria-describedby"]
        }
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
      {problem !== null ? (
        <div className="field-problem" id={problemId} role="alert">
          {problem}
        </div>
      ) : null}
    </>
  );
}
